import React, { useMemo } from "react";
import SignOffAccordionItem from "protocol-setup/components/SignOffAccordionItem";
import { getNextStatus, getStatusPosition } from "protocol-setup/helpers";
import ProtectionElements from "protocol-setup/helpers/ProtectionElement";
import { useStatusHistory } from "protocol-setup/redux/useLovs";

import RegulatorySubmitForm from "./RegulatorySubmitForm";
import RegulatoryAdditionalSignOff from "./RegulatoryAdditionalSignOff";
import RegulatoryInProgress from "./RegulatoryInProgress";
import RegulatoryCompleted from "./RegulatoryCompleted";


const getSubmitterInfo = (protocolSetupData, nextStatus) => {
  let data = {}
  switch (nextStatus) {
    case "ADDITIONAL_SIGNOFF":
      data = {
        submitterName: protocolSetupData?.regSignoff?.groupAdminSubmittedBy || '',
        submittedDate: protocolSetupData?.regSignoff?.groupAdminSubmittedDate || ''
      }
      break;
    case "IN_PROGRESS":
      data = {
        submitterName: protocolSetupData?.regSignoff?.irocAdminSubmittedBy || '',
        submittedDate: protocolSetupData?.regSignoff?.irocAdminSubmittedDate || ''
      }
      break;
    case "COMPLETED": {
      // For both In Progress and Completed, get the latest submitter from regSignoffLog (sorted)
      const regSignoffLog = protocolSetupData?.regSignoffLog || [];
      if (regSignoffLog.length > 0) {
        // Sort by date descending (newest first)
        const sortedLog = [...regSignoffLog].sort((a, b) => {
          if (!a.regSubmittedDt) return 1;
          if (!b.regSubmittedDt) return -1;
          return new Date(b.regSubmittedDt) - new Date(a.regSubmittedDt);
        });
        const latestSubmitter = sortedLog[0];
        data = {
          submitterName: latestSubmitter?.regSubmittedBy || '',
          submittedDate: latestSubmitter?.regSubmittedDt || ''
        };
      } else {
        data = {
          submitterName: '',
          submittedDate: ''
        };
      }
      break;
    }
    default:
      {
        const signOffInfo = protocolSetupData?.signOffInfo || [];
        const signOffEntry = signOffInfo.find(entry => entry.status === nextStatus);
        data = {
          submitterName: signOffEntry?.signedOffByName || '',
          submittedDate: signOffEntry?.signedOffDate || ''
        };
      }
      break;
  }
  return data;
}

const permissionMap = {
  SUBMITTED: ProtectionElements.REGULATORY_SIGNOFF_SUBMITTED,
  ADDITIONAL_SIGNOFF: ProtectionElements.REGULATORY_SIGNOFF_ADDITIONAL,
  IN_PROGRESS: ProtectionElements.REGULATORY_SIGNOFF_INPROGRESS,
  COMPLETED: ProtectionElements.OPEN_SIGNOFF_COMPLETE // Assuming default complete, or maybe null? Using OPEN_SIGNOFF_COMPLETE as per other usage or default.
};


function SignOffForms({ protocolSetupData, fetchPsData }) {
  const { formPartType, requestNumber, requestStatus: currentStatus, regSignoff } = protocolSetupData;
  const { statusHistory } = useStatusHistory(requestNumber);

  const FORM_COMPONENTS = useMemo(() => regSignoff?.irocAdminSignoffRequired ? {
    SUBMITTED: RegulatorySubmitForm,
    ADDITIONAL_SIGNOFF: RegulatoryAdditionalSignOff,
    IN_PROGRESS: RegulatoryInProgress,
    COMPLETED: RegulatoryCompleted
  } : {
    SUBMITTED: RegulatorySubmitForm,
    IN_PROGRESS: RegulatoryInProgress,
    COMPLETED: RegulatoryCompleted
  }, [regSignoff]);

  const PANEL_CONFIG = useMemo(() => regSignoff?.irocAdminSignoffRequired ? [
    { title: 'Submitted', status: 'SUBMITTED' },
    { title: 'Additional SignOff', status: 'ADDITIONAL_SIGNOFF' },
    { title: 'In Progress', status: 'IN_PROGRESS' },
    { title: 'Completed', status: 'COMPLETED' }
  ] : [
    { title: 'Submitted', status: 'SUBMITTED' },
    { title: 'In Progress', status: 'IN_PROGRESS' },
    { title: 'Completed', status: 'COMPLETED' }
  ], [regSignoff]);

  const requestStatus = useMemo(() => {
    let status = currentStatus;

    if ((status === 'IN_PROGRESS' && regSignoff?.irocAdminSignoffRequired) && regSignoff?.irocAdminSignoff === null) {
      status = 'ADDITIONAL_SIGNOFF'
    }

    if (currentStatus === "RETIRED" || currentStatus === "CANCELED") {
      const historyEntry = (statusHistory || []).find(
        (entry) => entry.toState === currentStatus
      );

      if (historyEntry && historyEntry.fromState) {
        const fromStatus = historyEntry.fromState;
        const fromStatusIndex = PANEL_CONFIG.findIndex(
          (panel) => panel.status === fromStatus
        );

        if (fromStatusIndex > 0) {
          status = PANEL_CONFIG[fromStatusIndex - 1].status;
        } else {
          status = fromStatus;
        }
      }
    }
    return status;
  }, [currentStatus, regSignoff, statusHistory, PANEL_CONFIG]);


  const currentDateTime = useMemo(() => new Date().toLocaleString(), []);
  const panels = useMemo(() =>
    PANEL_CONFIG.map(({ title, status }) => {
      const FormComponent = FORM_COMPONENTS[status];
      let statusIndex = getStatusPosition(requestStatus, status, formPartType);

      if ((status === 'IN_PROGRESS' && regSignoff?.irocAdminSignoffRequired) && regSignoff?.irocAdminSignoff === null && !regSignoff?.isRegAckCompletedYn) {
        statusIndex = 0; // Set to 0 to allow access to In Progress form when Additional SignOff is required but not completed.
      }

      if (status === 'IN_PROGRESS' && (requestStatus === 'ADDITIONAL_SIGNOFF' && regSignoff?.irocAdminSignoffRequired) && regSignoff?.isRegAckCompletedYn) {
        statusIndex = -1; // Set to -1 to allow access to In Progress form when Additional SignOff is required but not completed and inprogress is completed.
      }

      if (requestStatus === 'COMPLETED' && (status === 'ADDITIONAL_SIGNOFF' && regSignoff?.irocAdminSignoffRequired) && regSignoff?.irocAdminSignoff === null) {
        statusIndex = 0; // Set to 0 to allow access to In Progress form when Additional SignOff is required but not completed.
      }

      return {
        title,
        status,
        statusIndex,
        FormComponent
      };
    }),
    [requestStatus, formPartType, PANEL_CONFIG, FORM_COMPONENTS, regSignoff]
  );

  return (
    <>
      {panels.map(({ title, status, statusIndex, FormComponent }) => {
        let submitterData;
        if (status === 'COMPLETED' && requestStatus === 'COMPLETED') {
          submitterData = getSubmitterInfo(protocolSetupData, 'COMPLETED');
        } else if (status === 'COMPLETED') {
          submitterData = {};
        } else {
          submitterData = getSubmitterInfo(protocolSetupData, getNextStatus(status, formPartType));
        }

        return (
          <SignOffAccordionItem
            key={status}
            title={title}
            status={status}
            requestStatusIndex={statusIndex}
            currentDateTime={currentDateTime}
            requestStatus={requestStatus}
            data={submitterData}
            open={status === requestStatus}
            disable={statusIndex > 0 && !(status === 'COMPLETED' && requestStatus === 'COMPLETED')}
            currentStatus={currentStatus}
            enableHighlight={status !== 'COMPLETED'}
            protectionElement={permissionMap[status]}
          >
            <FormComponent
              protocolSetupData={protocolSetupData}
              fetchPsData={fetchPsData}
              readOnly={statusIndex < 0 || currentStatus === 'RETIRED' || currentStatus === 'CANCELED'}
            />
          </SignOffAccordionItem>
        );
      })}
    </>
  );
}

export default SignOffForms;

